import {
  Brain,
  Calculator,
  CalendarClock,
  LogOut,
  Moon,
  PanelLeft,
  PieChart,
  Plus,
  Settings as SettingsIcon,
  Sun,
  Users,
  Workflow,
} from 'lucide-react'
import { useStore } from '../../store'
import { cn } from '../../lib/utils'

function RailButton({
  icon,
  label,
  active,
  badge,
  onClick,
}: {
  icon: React.ReactNode
  label: string
  active?: boolean
  badge?: number
  onClick: () => void
}) {
  return (
    <button
      onClick={onClick}
      title={label}
      aria-label={label}
      className={cn(
        'relative flex h-10 w-10 items-center justify-center rounded-xl transition-colors',
        active
          ? 'bg-[var(--accent-wash)] text-fg'
          : 'text-fg-subtle hover:bg-[var(--color-surface-2)] hover:text-fg',
      )}
    >
      {icon}
      {badge != null && badge > 0 && (
        <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-[var(--color-accent)] px-1 text-[9px] font-bold text-[var(--color-base)]">
          {badge > 9 ? '9+' : badge}
        </span>
      )}
    </button>
  )
}

export function Rail() {
  const newChat = useStore((s) => s.newChat)
  const sidebarOpen = useStore((s) => s.sidebarOpen)
  const toggleSidebar = useStore((s) => s.toggleSidebar)
  const calculatorOpen = useStore((s) => s.calculatorOpen)
  const toggleCalculator = useStore((s) => s.toggleCalculator)
  const memoryOpen = useStore((s) => s.memoryOpen)
  const toggleMemory = useStore((s) => s.toggleMemory)
  const theme = useStore((s) => s.theme)
  const setTheme = useStore((s) => s.setTheme)
  const user = useStore((s) => s.user)
  const adminOpen = useStore((s) => s.adminOpen)
  const toggleAdmin = useStore((s) => s.toggleAdmin)
  const logout = useStore((s) => s.logout)
  const view = useStore((s) => s.view)
  const setView = useStore((s) => s.setView)
  const openWorker = useStore((s) => s.openWorker)
  const taskUnread = useStore((s) => s.taskUnread)

  return (
    <nav className="hidden h-full w-14 shrink-0 flex-col items-center gap-1 border-r border-[var(--line-soft)] bg-[var(--color-void)] py-3 md:flex">
      <RailButton icon={<Plus size={19} />} label="New chat" onClick={newChat} />
      <RailButton
        icon={<PanelLeft size={18} />}
        label="Chat history"
        active={sidebarOpen}
        onClick={() => toggleSidebar()}
      />
      <RailButton
        icon={<PieChart size={18} />}
        label="Portfolio"
        active={view === 'portfolio'}
        onClick={() => setView('portfolio')}
      />
      <RailButton
        icon={<CalendarClock size={18} />}
        label="Tasks"
        active={view === 'tasks'}
        badge={taskUnread}
        onClick={() => setView('tasks')}
      />
      {user?.role === 'admin' && (
        <RailButton icon={<Workflow size={18} />} label="Workers" active={view === 'workers'} onClick={() => openWorker(null)} />
      )}
      <RailButton
        icon={<Calculator size={18} />}
        label="Scientific calculator"
        active={calculatorOpen}
        onClick={() => toggleCalculator()}
      />
      <RailButton
        icon={<Brain size={18} />}
        label="Memory"
        active={memoryOpen}
        onClick={() => toggleMemory()}
      />
      <div className="flex-1" />
      <RailButton
        icon={<SettingsIcon size={18} />}
        label="Settings"
        active={view === 'settings'}
        onClick={() => setView('settings')}
      />
      <RailButton
        icon={theme === 'dark' ? <Sun size={18} /> : <Moon size={17} />}
        label="Toggle theme"
        onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
      />
      {user?.role === 'admin' && (
        <RailButton
          icon={<Users size={18} />}
          label="Users & usage"
          active={adminOpen}
          onClick={() => toggleAdmin()}
        />
      )}
      <RailButton icon={<LogOut size={17} />} label={`Sign out (${user?.username ?? ''})`} onClick={() => void logout()} />
    </nav>
  )
}
